import { Typewriter } from '@/components/ui/typewriter';
import { Gravity, MatterBody } from '@/components/ui/gravity';

interface BallProps {
  icon: string;
  label: string;
  color: string;
  size?: 'sm' | 'md' | 'lg';
}

const RAINBOW_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#10B981', // emerald
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#F43F5E', // rose
  '#FB923C', // light orange
  '#4ADE80', // light green
  '#2DD4BF', // light teal
  '#38BDF8', // light blue
  '#818CF8', // light indigo
  '#C084FC', // light violet
  '#F472B6', // light pink
];

const TechBall = ({ icon, label, color, size = 'md' }: BallProps) => {
  const sizeMap = {
    sm: 'w-[72px] h-[72px]',
    md: 'w-[88px] h-[88px]',
    lg: 'w-[104px] h-[104px]',
  };
  const iconMap = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };
  const labelMap = {
    sm: 'text-[9px]',
    md: 'text-[10px]',
    lg: 'text-[11px]',
  };
  return (
    <div
      className={`${sizeMap[size]} rounded-full flex flex-col items-center justify-center gap-0.5 select-none shadow-md text-white`}
      style={{ backgroundColor: color }}
    >
      <i className={`${icon} ${iconMap[size]}`}></i>
      <span className={`${labelMap[size]} font-mono font-semibold whitespace-nowrap leading-tight`}>{label}</span>
    </div>
  );
};

const HeroSection = () => {
  return (
    <section className="relative w-full min-h-[720px] bg-background-100 overflow-hidden">
      {/* ── Full-width Gravity Background ── */}
      <div className="absolute inset-0 z-0">
        <Gravity gravity={{ x: 0, y: 0.65 }} hoverRepel={true} repelRadius={240} repelStrength={0.25} className="w-full h-full">

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-1.5} initialVelocityY={2} x="58%" y="5%">
            <TechBall icon="ri-cloud-line" label="Cloud" color={RAINBOW_COLORS[0]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={2} initialVelocityY={-3} x="74%" y="7%">
            <TechBall icon="ri-shield-check-line" label="Security" color={RAINBOW_COLORS[1]} size="lg" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-3} initialVelocityY={1.5} x="91%" y="5%">
            <TechBall icon="ri-sparkling-line" label="AI / ML" color={RAINBOW_COLORS[2]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={1} initialVelocityY={-2.5} x="65%" y="10%">
            <TechBall icon="ri-reactjs-line" label="React" color={RAINBOW_COLORS[3]} size="sm" />
          </MatterBody>

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={2.5} initialVelocityY={-2} x="82%" y="14%">
            <TechBall icon="ri-stack-line" label="Full-Stack" color={RAINBOW_COLORS[4]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-2} initialVelocityY={3} x="56%" y="19%">
            <TechBall icon="ri-database-2-line" label="Data" color={RAINBOW_COLORS[5]} size="lg" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-1} initialVelocityY={-3.5} x="93%" y="17%">
            <TechBall icon="ri-code-s-slash-line" label="Python" color={RAINBOW_COLORS[6]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={3} initialVelocityY={1} x="71%" y="23%">
            <TechBall icon="ri-server-line" label="Node.js" color={RAINBOW_COLORS[7]} />
          </MatterBody>

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-2.5} initialVelocityY={-2} x="60%" y="31%">
            <TechBall icon="ri-global-line" label="AWS" color={RAINBOW_COLORS[8]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={1.5} initialVelocityY={2.5} x="88%" y="27%">
            <TechBall icon="ri-ship-line" label="Docker" color={RAINBOW_COLORS[9]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-1} initialVelocityY={-3} x="77%" y="37%">
            <TechBall icon="ri-code-box-line" label="TypeScript" color={RAINBOW_COLORS[10]} size="lg" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={3.5} initialVelocityY={2} x="95%" y="35%">
            <TechBall icon="ri-terminal-box-line" label="Go" color={RAINBOW_COLORS[11]} size="sm" />
          </MatterBody>

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-3} initialVelocityY={-1.5} x="63%" y="47%">
            <TechBall icon="ri-database-line" label="PostgreSQL" color={RAINBOW_COLORS[12]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={2} initialVelocityY={3.5} x="85%" y="45%">
            <TechBall icon="ri-settings-4-line" label="DevOps" color={RAINBOW_COLORS[13]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-1.5} initialVelocityY={-2.5} x="57%" y="53%">
            <TechBall icon="ri-plug-line" label="API" color={RAINBOW_COLORS[14]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={2.5} initialVelocityY={1} x="92%" y="50%">
            <TechBall icon="ri-node-tree" label="GraphQL" color={RAINBOW_COLORS[15]} />
          </MatterBody>

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-2} initialVelocityY={-3} x="75%" y="63%">
            <TechBall icon="ri-flashlight-line" label="Redis" color={RAINBOW_COLORS[16]} size="lg" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={3} initialVelocityY={2} x="61%" y="69%">
            <TechBall icon="ri-git-branch-line" label="GitHub" color={RAINBOW_COLORS[17]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-3.5} initialVelocityY={-1} x="87%" y="67%">
            <TechBall icon="ri-leaf-line" label="MongoDB" color={RAINBOW_COLORS[18]} size="sm" />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={1} initialVelocityY={3} x="94%" y="73%">
            <TechBall icon="ri-terminal-line" label="Linux" color={RAINBOW_COLORS[19]} size="sm" />
          </MatterBody>

          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={-1} initialVelocityY={-2} x="69%" y="81%">
            <TechBall icon="ri-earth-line" label="Terraform" color={RAINBOW_COLORS[20]} />
          </MatterBody>
          <MatterBody shape="circle" matterBodyOptions={{ friction: 0.18, restitution: 0.6 }} initialVelocityX={2} initialVelocityY={-3.5} x="84%" y="85%">
            <TechBall icon="ri-settings-6-line" label="K8s" color={RAINBOW_COLORS[21]} size="sm" />
          </MatterBody>
        </Gravity>
      </div>

      {/* ── Content Layer (on top of gravity) ── */}
      <div className="section-padding relative z-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Column - Text */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground-900 text-background-50 text-[10px] font-mono uppercase tracking-widest">
                  <i className="ri-shield-flash-line text-sm"></i>
                  Est. 2025
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-foreground-600">
                  / IT Solutions
                </span>
              </div>

              <h1 className="font-display text-4xl md:text-5xl xl:text-6xl font-bold leading-[0.95] tracking-tight text-foreground-900">
                Driving Business
                <span className="block text-primary-500">
                  Success Through{' '}
                  <Typewriter
                    text={['Technology', 'Innovation', 'Excellence', 'Strategy']}
                    speed={80}
                    deleteSpeed={45}
                    waitTime={1800}
                    loop={true}
                    cursorChar="|"
                    cursorClassName="text-primary-500/60 font-thin"
                  />
                </span>
              </h1>

              <p className="mt-6 text-[15px] leading-relaxed text-foreground-700 max-w-md">
                We are a forward-thinking IT company delivering reliable, scalable, and secure digital solutions for modern businesses worldwide.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-4 max-w-sm">
                <div className="border-t border-foreground-900/80 pt-3">
                  <div className="font-display text-3xl font-bold text-foreground-900">
                    20<span className="text-primary-500">+</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-600 mt-1">
                    Years
                  </div>
                </div>
                <div className="border-t border-foreground-900/80 pt-3">
                  <div className="font-display text-3xl font-bold text-foreground-900">
                    50<span className="text-primary-500">+</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-600 mt-1">
                    Projects
                  </div>
                </div>
                <div className="border-t border-foreground-900/80 pt-3">
                  <div className="font-display text-3xl font-bold text-foreground-900">
                    98<span className="text-primary-500">%</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-600 mt-1">
                    Satisfaction
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - empty */}
            <div className="hidden lg:block" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;